import { useEffect, useState } from "react";
import StreamVideo from "@/components/StreamVideo";
import { useNavigate, useParams } from "react-router-dom";
import api, { naira, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Save, AlertTriangle, Film as FilmIcon, Upload, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import GenreFields from "@/components/GenreFields";
const TRAILER_MAX_MB = 100;
const ACCEPTED_TRAILER_EXT = [".mp4", ".m4v"];

const PRICE_TIERS = [
  { amount: 100, premiering_only: false },
  { amount: 200, premiering_only: false },
  { amount: 300, premiering_only: false },
  { amount: 500, premiering_only: true },
  { amount: 1000, premiering_only: true },
];

const USD_DEFAULTS_CENTS = {
  100:  { african: 25,  diaspora: 100 },
  200:  { african: 50,  diaspora: 200 },
  300:  { african: 75,  diaspora: 300 },
  500:  { african: 125, diaspora: 500 },
  1000: { african: 250, diaspora: 1000 },
};
const usdFromCents = (c) => `$${(Number(c) / 100).toFixed(2)}`;

export default function EditFilm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [film, setFilm] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trailerUploading, setTrailerUploading] = useState(false);
  const [trailerProgress, setTrailerProgress] = useState(0);
  const [trailerDeleting, setTrailerDeleting] = useState(false);
  // Cache-busts the <video> so a freshly-uploaded trailer reloads instantly
  // instead of showing the browser-cached previous one.
  const [trailerCacheBust, setTrailerCacheBust] = useState(0);

  useEffect(() => {
    api.get(`/films/${id}`).then((r) => {
      setFilm(r.data);
      setForm({
        title: r.data.title,
        synopsis: r.data.synopsis,
        genre: r.data.genre,
        sub_genre: r.data.sub_genre || "",
        duration_minutes: r.data.duration_minutes,
        price_naira: r.data.price_naira,
        african_price_usd_cents: r.data.african_price_usd_cents ?? (USD_DEFAULTS_CENTS[r.data.price_naira]?.african ?? 50),
        diaspora_price_usd_cents: r.data.diaspora_price_usd_cents ?? (USD_DEFAULTS_CENTS[r.data.price_naira]?.diaspora ?? 200),
        premiering: Boolean(r.data.premiering),
        themes: (r.data.themes || []).join(", "),
        language: r.data.language,
        country: r.data.country,
        year: r.data.year,
        director: r.data.director || "",
        producer: r.data.producer || "",
        cast: r.data.cast || "",
        instagram_handle: r.data.instagram_handle || "",
        twitter_handle: r.data.twitter_handle || "",
      });
    });
  }, [id]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const uploadTrailer = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!ACCEPTED_TRAILER_EXT.some((ext) => lower.endsWith(ext))) {
      toast.error("Trailer must be an MP4 (H.264). Re-encode using HandBrake if needed.");
      return;
    }
    if (file.size > TRAILER_MAX_MB * 1024 * 1024) {
      toast.error(`Trailer is ${(file.size / 1024 / 1024).toFixed(1)} MB — max ${TRAILER_MAX_MB} MB.`);
      return;
    }
    setTrailerUploading(true);
    setTrailerProgress(0);
    const fd = new FormData();
    fd.append("trailer", file);
    try {
      const { data } = await api.post(`/films/${id}/trailer`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setTrailerProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setFilm(data);
      setTrailerCacheBust(Date.now());
      toast.success("Trailer uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Trailer upload failed");
    } finally {
      setTrailerUploading(false);
      setTrailerProgress(0);
    }
  };

  const deleteTrailer = async () => {
    if (!window.confirm("Delete the current trailer? You can upload a new one any time.")) return;
    setTrailerDeleting(true);
    try {
      await api.delete(`/films/${id}/trailer`);
      const { data } = await api.get(`/films/${id}`);
      setFilm(data);
      toast.success("Trailer removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove trailer");
    } finally {
      setTrailerDeleting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const themesArr = form.themes
        ? form.themes.split(",").map((t) => t.trim()).filter(Boolean)
        : [];
      await api.patch(`/films/${id}`, { ...form, themes: themesArr });
      toast.success(film.status === "rejected" ? "Updated and resubmitted for review." : "Film updated.");
      navigate("/filmmaker");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (!film || !form) {
    return (
      <div className="min-h-screen bg-ink grain">
        <Navbar />
        <div className="pt-32 px-8 text-center text-foreground/60">Loading…</div>
      </div>
    );
  }

  // Filmmakers cannot edit approved films (the backend enforces this too).
  // Admins always have full edit rights — this gate is just UX clarity.
  const isApproved = film.status === "approved";
  const editLocked = isApproved && !isAdmin;

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-4xl mx-auto pb-20">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">Filmmaker</p>
          <h1 className="font-display text-4xl font-black">Edit film</h1>
          <p className="text-foreground/60 mt-2">{film.title}</p>
        </div>

        {editLocked && (
          <div className="mb-6 p-4 rounded-lg border border-gold/30 bg-gold/10 flex items-start gap-3" data-testid="edit-blocked-banner">
            <AlertTriangle className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/80">
              This film is already <strong>approved and live</strong>. Editing approved films is disabled to keep the catalogue stable. Contact admin if you need a change.
            </p>
          </div>
        )}

        {isApproved && isAdmin && (
          <div className="mb-6 p-4 rounded-lg border border-terracotta/40 bg-terracotta/10 flex items-start gap-3" data-testid="admin-edit-banner">
            <AlertTriangle className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/85">
              You&apos;re editing a <strong>live, approved</strong> film as admin. Changes take effect immediately for every viewer. The action is audit-logged.
            </p>
          </div>
        )}

        {film.status === "rejected" && film.admin_note && (
          <div className="mb-6 p-4 rounded-lg border border-white/10 bg-surface">
            <p className="text-xs uppercase tracking-widest text-red-400 font-semibold mb-1">Admin note</p>
            <p className="text-sm text-foreground/80 italic">&ldquo;{film.admin_note}&rdquo;</p>
            <p className="text-xs text-foreground/50 mt-2">Saving changes will resubmit your film for review.</p>
          </div>
        )}

        {/* TRAILER MANAGER — available regardless of approval status */}
        <div className="mb-6 bg-surface border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="trailer-manager">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-widest text-gold font-semibold flex items-center gap-1.5">
                <FilmIcon className="w-3 h-3" /> Trailer
              </p>
              <h2 className="font-display text-xl font-bold mt-1">Marketing clip</h2>
              <p className="text-foreground/60 text-sm mt-1">
                Optional. A short MP4 viewers can watch before renting — typically 30–90 seconds. Max {TRAILER_MAX_MB} MB.
              </p>
            </div>
            {film.trailer_path && (
              <Button
                type="button"
                onClick={deleteTrailer}
                disabled={trailerDeleting || trailerUploading}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9"
                data-testid="trailer-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5" /> {trailerDeleting ? "Removing…" : "Remove trailer"}
              </Button>
            )}
          </div>

          {(film.trailer_path || film.bunny_trailer_status === "ready") && (
            <div className="aspect-video rounded-lg overflow-hidden bg-black border border-white/10 mb-4">
              <StreamVideo
                key={film.trailer_uploaded_at || trailerCacheBust}
                playbackPath={`/films/${id}/trailer-playback`}
                legacySrc={`${API}/films/${id}/trailer?v=${encodeURIComponent(film.trailer_uploaded_at || trailerCacheBust)}`}
                controls
                playsInline
                preload="metadata"
                controlsList="nodownload"
                className="w-full h-full"
                data-testid="trailer-current-preview"
              />
            </div>
          )}

          <label
            className={`mt-1 flex flex-col items-center justify-center h-28 rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
              trailerUploading
                ? "border-gold/50 bg-gold/5"
                : "border-white/10 hover:border-terracotta bg-ink"
            }`}
            data-testid="trailer-upload-zone"
          >
            <Upload className={`w-5 h-5 mb-1 ${trailerUploading ? "text-gold animate-pulse" : "text-foreground/40"}`} />
            <span className="text-xs text-foreground/70 px-3 text-center">
              {trailerUploading
                ? `Uploading… ${trailerProgress}%`
                : film.trailer_path
                ? "Click to replace the current trailer"
                : "Click to choose an MP4 trailer"}
            </span>
            <input
              type="file"
              accept=".mp4,.m4v,video/mp4"
              hidden
              disabled={trailerUploading}
              onChange={(e) => uploadTrailer(e.target.files?.[0])}
            />
          </label>
        </div>

        <form onSubmit={submit} className="space-y-6 bg-surface border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="edit-film-form">
          <fieldset disabled={editLocked} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} required className="mt-1.5 bg-ink border-white/10 h-11" data-testid="edit-title-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>Synopsis</Label>
                <Textarea value={form.synopsis} onChange={(e) => set("synopsis", e.target.value)} required rows={4} className="mt-1.5 bg-ink border-white/10" data-testid="edit-synopsis-input" />
              </div>
              <GenreFields genre={form.genre} subGenre={form.sub_genre} onChange={set} testPrefix="edit" />
              <div>
                <Label>Duration (minutes)</Label>
                <Input type="number" min={1} max={45} value={form.duration_minutes} onChange={(e) => set("duration_minutes", Number(e.target.value))} required className="mt-1.5 bg-ink border-white/10 h-11" />
              </div>
              <div>
                <Label>Language</Label>
                <Input value={form.language} onChange={(e) => set("language", e.target.value)} className="mt-1.5 bg-ink border-white/10 h-11" />
              </div>
              <div>
                <Label>Country</Label>
                <Input value={form.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 bg-ink border-white/10 h-11" />
              </div>
              <div>
                <Label>Year</Label>
                <Input type="number" min={1980} max={new Date().getFullYear()} value={form.year} onChange={(e) => set("year", Number(e.target.value))} className="mt-1.5 bg-ink border-white/10 h-11" />
              </div>
              <div className="sm:col-span-2">
                <Label>Themes (comma-separated)</Label>
                <Input value={form.themes} onChange={(e) => set("themes", e.target.value)} className="mt-1.5 bg-ink border-white/10 h-11" data-testid="edit-themes-input" />
              </div>
              <div className="sm:col-span-2 pt-3 mt-1 border-t border-white/5">
                <p className="text-[11px] uppercase tracking-widest text-foreground/45 font-semibold mb-1">Credits &amp; socials</p>
              </div>
              <div>
                <Label>Director</Label>
                <Input value={form.director} onChange={(e) => set("director", e.target.value)} placeholder="e.g. Tunde Adebayo" className="mt-1.5 bg-ink border-white/10 h-11" data-testid="edit-director-input" />
              </div>
              <div>
                <Label>Producer</Label>
                <Input value={form.producer} onChange={(e) => set("producer", e.target.value)} placeholder="e.g. Amina Bello" className="mt-1.5 bg-ink border-white/10 h-11" data-testid="edit-producer-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>Cast</Label>
                <Input value={form.cast} onChange={(e) => set("cast", e.target.value)} placeholder="Lead names, comma-separated" className="mt-1.5 bg-ink border-white/10 h-11" data-testid="edit-cast-input" />
              </div>
              <div>
                <Label>Instagram <span className="text-foreground/40 font-normal">(optional)</span></Label>
                <div className="mt-1.5 flex items-center rounded-md bg-ink border border-white/10 h-11 overflow-hidden focus-within:border-terracotta/60">
                  <span className="px-3 text-sm text-foreground/45 select-none">@</span>
                  <Input value={form.instagram_handle} onChange={(e) => set("instagram_handle", e.target.value)} placeholder="your_handle" className="flex-1 bg-transparent border-0 h-11 px-0 focus-visible:ring-0" data-testid="edit-instagram-input" />
                </div>
              </div>
              <div>
                <Label>Twitter / X <span className="text-foreground/40 font-normal">(optional)</span></Label>
                <div className="mt-1.5 flex items-center rounded-md bg-ink border border-white/10 h-11 overflow-hidden focus-within:border-terracotta/60">
                  <span className="px-3 text-sm text-foreground/45 select-none">@</span>
                  <Input value={form.twitter_handle} onChange={(e) => set("twitter_handle", e.target.value)} placeholder="your_handle" className="flex-1 bg-transparent border-0 h-11 px-0 focus-visible:ring-0" data-testid="edit-twitter-input" />
                </div>
              </div>
              <div className="sm:col-span-2 rounded-xl border border-white/10 bg-ink/40 p-4 space-y-4">
                {/* Premiering toggle — gates premium pricing + lands the
                    title on the landing-page Premiering strip. */}
                <label className="flex items-start gap-3 cursor-pointer" data-testid="edit-premiering-row">
                  <Checkbox
                    checked={form.premiering}
                    onCheckedChange={(v) => {
                      const next = Boolean(v);
                      setForm((p) => {
                        const needsDrop = !next && (p.price_naira === 500 || p.price_naira === 1000);
                        const nextNaira = needsDrop ? 300 : p.price_naira;
                        const defaults = USD_DEFAULTS_CENTS[nextNaira] || USD_DEFAULTS_CENTS[200];
                        return {
                          ...p,
                          premiering: next,
                          price_naira: nextNaira,
                          african_price_usd_cents: needsDrop ? defaults.african : p.african_price_usd_cents,
                          diaspora_price_usd_cents: needsDrop ? defaults.diaspora : p.diaspora_price_usd_cents,
                        };
                      });
                    }}
                    className="mt-0.5 border-gold data-[state=checked]:bg-gold data-[state=checked]:text-ink"
                    data-testid="edit-premiering-toggle"
                  />
                  <span className="flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-semibold">
                      <Sparkles className="w-3.5 h-3.5 text-gold" /> Premiering title
                    </span>
                    <span className="block text-[11px] text-foreground/55 leading-relaxed mt-0.5">
                      Appears in the Premiering row on the landing page and unlocks ₦500 / ₦1000 pricing.
                    </span>
                  </span>
                </label>

                <div>
                  <Label>Pricing tier</Label>
                  <div className="mt-2 grid grid-cols-5 gap-1.5" data-testid="edit-price-tiers">
                    {PRICE_TIERS.map((t) => {
                      const locked = t.premiering_only && !form.premiering;
                      const active = form.price_naira === t.amount;
                      return (
                        <button
                          type="button"
                          key={t.amount}
                          disabled={locked}
                          onClick={() => {
                            const d = USD_DEFAULTS_CENTS[t.amount];
                            setForm((p) => ({
                              ...p,
                              price_naira: t.amount,
                              african_price_usd_cents: d.african,
                              diaspora_price_usd_cents: d.diaspora,
                            }));
                          }}
                          className={[
                            "h-10 rounded-md border text-xs font-semibold transition-all",
                            active ? "bg-gold text-ink border-gold" : "bg-ink border-white/10 text-foreground/80 hover:border-gold/50",
                            locked ? "opacity-40 cursor-not-allowed hover:border-white/10" : "",
                          ].join(" ")}
                          data-testid={`edit-price-${t.amount}`}
                        >
                          ₦{t.amount}
                          {t.premiering_only && <Sparkles className="inline w-2.5 h-2.5 ml-0.5 -mt-0.5 text-gold" />}
                        </button>
                      );
                    })}
                  </div>
                  {!form.premiering && (
                    <p className="mt-2 text-[11px] text-foreground/50">
                      ₦500 and ₦1000 unlock when Premiering is on.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-white/5">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-foreground/55">Local (Nigeria)</Label>
                    <div className="mt-1.5 h-10 rounded-md bg-ink/60 border border-white/10 flex items-center px-3 text-gold font-display font-bold text-sm" data-testid="edit-price-local">
                      {naira(form.price_naira)}
                    </div>
                    <p className="text-[10px] text-foreground/40 mt-1">From tier above</p>
                  </div>
                  <div>
                    <Label htmlFor="edit-african-usd" className="text-[11px] uppercase tracking-wider text-foreground/55">
                      African <span className="text-foreground/35 normal-case">(rest of Africa)</span>
                    </Label>
                    <div className="relative mt-1.5">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">$</span>
                      <input
                        id="edit-african-usd"
                        type="number"
                        min="0.01"
                        max="1000"
                        step="0.01"
                        value={(form.african_price_usd_cents / 100).toFixed(2)}
                        onChange={(e) => {
                          const cents = Math.max(1, Math.round(parseFloat(e.target.value || "0") * 100));
                          set("african_price_usd_cents", cents);
                        }}
                        className="h-10 w-full rounded-md bg-ink border border-white/10 pl-6 pr-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/40"
                        data-testid="edit-african-usd-input"
                      />
                    </div>
                    <p className="text-[10px] text-foreground/40 mt-1">Default: {usdFromCents((USD_DEFAULTS_CENTS[form.price_naira] || USD_DEFAULTS_CENTS[200]).african)}</p>
                  </div>
                  <div>
                    <Label htmlFor="edit-diaspora-usd" className="text-[11px] uppercase tracking-wider text-foreground/55">
                      Diaspora <span className="text-foreground/35 normal-case">(US / UK / EU)</span>
                    </Label>
                    <div className="relative mt-1.5">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">$</span>
                      <input
                        id="edit-diaspora-usd"
                        type="number"
                        min="0.01"
                        max="1000"
                        step="0.01"
                        value={(form.diaspora_price_usd_cents / 100).toFixed(2)}
                        onChange={(e) => {
                          const cents = Math.max(1, Math.round(parseFloat(e.target.value || "0") * 100));
                          set("diaspora_price_usd_cents", cents);
                        }}
                        className="h-10 w-full rounded-md bg-ink border border-white/10 pl-6 pr-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/40"
                        data-testid="edit-diaspora-usd-input"
                      />
                    </div>
                    <p className="text-[10px] text-foreground/40 mt-1">Default: {usdFromCents((USD_DEFAULTS_CENTS[form.price_naira] || USD_DEFAULTS_CENTS[200]).diaspora)}</p>
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" disabled={loading || editLocked} className="w-full bg-terracotta hover:bg-ember text-white h-12" data-testid="edit-submit-btn">
              <Save className="w-4 h-4" /> {loading ? "Saving…" : film.status === "rejected" ? "Save & resubmit for review" : "Save changes"}
            </Button>
          </fieldset>
        </form>
      </div>
    </div>
  );
}
